import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import { AdsTargetingFilter } from '~/data/models/admin';
import { CampaignDetails, Regions } from '~/data/models/campaign';
import { INSTITUTES_TARGETING } from '~/data/operations/admin';
import {
  ASSET,
  CAMPAIGN,
  DELETE_BANNER_ASSET,
  DUPLICATE_BANNER_ASSET,
  EDIT_BANNER_ASSET,
  REGIONS,
} from '~/data/operations/campaign';
import { writeCampaignQuery } from '~/data/operations/campaign/helpers';
import { GRADUATION_TYPES } from '~/data/operations/group';
import { INSTITUTE_TYPES } from '~/data/operations/institute';
import {
  EditBannerAdAssetInput,
  TargetingFilterInput,
  DuplicateAssetInput,
  DeleteAssetInput,
} from '~/data/types/graphql';
import { AdminStackParamList, AdsStackParamList } from '~/navigation/types';
import { removeKeyFromObject } from '~/utils/helpers';
import { t } from '~/utils/i18n';
import { getImageBlob } from '../EditAd/utils';
import EditAssetLayout from './layout';

type EditAssetRouteProp = RouteProp<AdsStackParamList, 'EditAsset'>;
export default function EditAsset(): JSX.Element {
  type EditAdNavProp = StackNavigationProp<AdsStackParamList, 'EditAsset'>;
  const navigation = useNavigation<EditAdNavProp>();
  const [updateAsset] = useMutation(EDIT_BANNER_ASSET);
  const [duplicateAsset] = useMutation(DUPLICATE_BANNER_ASSET, {
    update(cache, { data }) {
      const oldAssets = campaignData?.campaign?.assets ?? [];
      const newAsset = data?.duplicateAsset?.newAsset;
      const vars = { id: campaignId };

      const updatedAssets = newAsset
        ? [...oldAssets, newAsset]
        : [...oldAssets];

      if (!campaignData?.campaign) {
        return;
      }
      const updatedCampaign: CampaignDetails = {
        ...campaignData?.campaign,
        assets: updatedAssets,
      };

      writeCampaignQuery({
        cache,
        variables: vars,
        data: {
          __typename: 'Query',
          campaign: updatedCampaign,
        },
      });
    },
  });

  const [deleteAsset] = useMutation(DELETE_BANNER_ASSET, {
    update(cache, { data }) {
      const deletedAsset = data?.deleteAsset?.assetId;
      const oldAssets = campaignData?.campaign?.assets ?? [];
      const vars = { id: campaignId };

      const updatedOldAssets = oldAssets.filter(
        (asset) => asset?.id !== deletedAsset,
      );
      if (!campaignData?.campaign) {
        return;
      }
      const updatedCampaign: CampaignDetails = {
        ...campaignData?.campaign,
        assets: updatedOldAssets,
      };

      writeCampaignQuery({
        cache,
        variables: vars,
        data: { __typename: 'Query', campaign: updatedCampaign },
      });
    },
  });

  const {
    params: { id, campaignName, campaignId },
  } = useRoute<EditAssetRouteProp>();

  const { data: campaignData } = useQuery(CAMPAIGN, {
    variables: { id: campaignId },
  });

  const { data: assetData, loading: assetLoading } = useQuery(ASSET, {
    variables: {
      id,
    },
  });
  const [filters, setFilters] = useState<AdsTargetingFilter[]>(
    assetData?.asset?.targetingFilters as AdsTargetingFilter[],
  );

  const { data: regionsData } = useQuery(REGIONS);

  const filteredTargetingFilters = removeKeyFromObject(
    filters ?? ({} as object),
    '__typename',
  );
  const { data: targetingData, loading: targetingLoading } = useQuery(
    INSTITUTES_TARGETING,
    {
      variables: {
        targetingFilters: filteredTargetingFilters,
      },
    },
  );

  const { openDrawer } =
    useNavigation<DrawerNavigationProp<AdminStackParamList>>();

  const { data: instituteTypes } = useQuery(INSTITUTE_TYPES, {
    variables: {
      first: 50,
    },
  });

  const { data: graduationTypes } = useQuery(GRADUATION_TYPES, {
    variables: {
      first: 50,
    },
  });

  // Function to prepare images for the API
  const prepareImagesForApi = async ({
    smallImage,
    largeImage,
  }: {
    smallImage: string;
    largeImage: string;
  }) => {
    const regex = /^(https|http)/;
    return {
      smallImage: regex.test(smallImage)
        ? null
        : await getImageBlob(smallImage),
      largeImage: regex.test(largeImage)
        ? null
        : await getImageBlob(largeImage),
    };
  };

  const onUpdateAsset = async ({
    id,
    visibility,
    startDate,
    endDate,
    assetLargeImage,
    assetSmallImage,
    targetingFilters,
    trackingUrl,
  }: EditBannerAdAssetInput) => {
    const filteredTargetingFilters = removeKeyFromObject(
      targetingFilters as object,
      '__typename',
    );
    const { smallImage, largeImage } = await prepareImagesForApi({
      smallImage: assetSmallImage,
      largeImage: assetLargeImage,
    });

    try {
      const { data } = await updateAsset({
        variables: {
          input: {
            id,
            visibility,
            startDate: startDate ? new Date(startDate).toISOString() : null,
            endDate: endDate ? new Date(endDate).toISOString() : null,
            assetLargeImage: largeImage,
            assetSmallImage: smallImage,
            targetingFilters:
              filteredTargetingFilters as TargetingFilterInput[],
            trackingUrl,
          },
        },
      });
      if (data) {
        Snackbar.show(t('editAsset.assetUpdated'));
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const onDuplicateAsset = async ({ assetId }: DuplicateAssetInput) => {
    try {
      const { data } = await duplicateAsset({
        variables: {
          input: {
            assetId,
          },
        },
      });
      if (data) {
        Snackbar.show(t('editAsset.assetDuplicated'));
      }
      navigation.navigate('EditAd', { id: campaignId });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const onDeleteAsset = async ({ assetId }: DeleteAssetInput) => {
    try {
      const { data } = await deleteAsset({
        variables: {
          input: {
            assetId,
          },
        },
      });
      if (data) {
        Snackbar.show(t('editAsset.assetDeleted'));
      }
      navigation.navigate('EditAd', { id: campaignId });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  return (
    <EditAssetLayout
      asset={assetData}
      campaignName={campaignName}
      onBack={() => navigation.goBack()}
      openDrawer={openDrawer}
      regions={regionsData?.regions as Regions[]}
      filters={filters as TargetingFilterInput[]}
      setFilters={setFilters}
      instituteTypes={instituteTypes}
      graduationTypes={graduationTypes}
      onUpdateAsset={onUpdateAsset}
      onDuplicateAsset={onDuplicateAsset}
      onDeleteAsset={onDeleteAsset}
      loading={assetLoading}
      targetingData={targetingData}
      targetingLoading={targetingLoading}
    />
  );
}
